import {Component, Input, OnChanges, SimpleChanges, ViewChild} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, NgForm, Validators} from '@angular/forms';
import {AddressModel, ApplicantModel, EmploymentModel, KeyIdentifierModel} from '@service/models';
import {AppConfigService} from '@service/config/app-config.service';
import {SharedkeyDataService} from '@service/services/common-services/sharedkey-data.service';
import {AddressComponent} from '@shared/form-components/address/address.component';
import {YearMonthFormFieldComponent} from '@shared/form-components/custom-form-fields/year-month/year-month-form-field.component';
import {ContentService} from '../../../constants/content.service';
import {getInstitutionId} from '../../../constants/constant';
import {environment} from '@env/environment';

@Component({
  selector: 'cs-residence-info',
  templateUrl: './residence-info.component.html',
  styleUrls: ['./residence-info.component.scss']
})
export class ResidenceInfoComponent implements OnChanges {
    @ViewChild('mailingAddress') mailingAddressCmp: AddressComponent;
    @ViewChild('yearmonth') yearMonthCmp: YearMonthFormFieldComponent;
    @ViewChild('mailingAddressForm') mailingAddressForm: NgForm;
    @Input() applicant: ApplicantModel;
    @Input() primaryApplicant: ApplicantModel;
    @Input() isCoBo: boolean = false;
    @Input('data-test-id') testId: string = 'residence-info';
    appConfig = AppConfigService.config;
    residenceForm: FormGroup;
    keyIdentifier: KeyIdentifierModel;
    finalSubmitPages: any;
    finalSubmitPageConfig: any;
    sameMailAddress = true;
    sameMailingAsPrimary = false;
    mailingAddressData: AddressModel = new AddressModel();
    private preSameAsPrimaryMailingAddressSnapshot: AddressModel = null;
    institutionSettings = getInstitutionId(environment.env);
    cssWidthClass = this.institutionSettings.singleColumnLayout ? 'col-sm-12' : 'col-md-6';

    constructor(public contentService: ContentService,
                private fb: FormBuilder,
                private sharedKeyDataService: SharedkeyDataService) {
        this.sharedKeyDataService.keyIdentifier$
            .subscribe((data) => {
                this.keyIdentifier = data;
                if (this.keyIdentifier.loanPurposeConfig) {
                    // TODO: remove backward compatability after moving to S3
				    this.finalSubmitPageConfig = this.keyIdentifier.loanPurposeConfig.finalSubmitPages?.page1 || this.keyIdentifier.loanPurposeConfig.finalSubmitPages;
                    this.finalSubmitPages = this.keyIdentifier.loanPurposeConfig.finalSubmitPages;
                }
            });

    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.applicant && changes.applicant.currentValue) {
            this.applicant = changes.applicant.currentValue;
            this.createForm();
        }
        if (changes.isCoBo && changes.isCoBo.currentValue) {
            this.isCoBo = !!changes.isCoBo.currentValue;
        }
    }

    runValidation() {
        this.residenceAddArray.markAllAsTouched();
        this.mailingAddressForm?.form.markAllAsTouched();
        this.yearMonthCmp?.markAsTouched();
        if (!this.sameMailingAsPrimary && !this.sameMailAddress && this.mailingAddressCmp) { this.mailingAddressCmp.touch(); }
    }

    get residenceAddArray() {
        return this.residenceForm.controls['residenceAdd'] as FormArray;
    }

    get primaryMailingAddress(): AddressModel {
        if (!this.primaryApplicant) { return null; }
        const primaryAdditionalAddresses: AddressModel[] = this.primaryApplicant.additionalAddressDetails || [];
        return primaryAdditionalAddresses.find(item => item.addressUsage === 'COMMUNICATION') || this.primaryApplicant.address;
    }

    onSameAsPrimaryAddressChange() {
        if (this.sameMailingAsPrimary) {
            if (!this.primaryMailingAddress) { return; }
            this.preSameAsPrimaryMailingAddressSnapshot = this.mailingAddressData;
            // Keep mailingAddressData in sync too, so any consumer reading it directly (rather than
            // checking sameMailingAsPrimary/primaryMailingAddress first) still sees the right address.
            // Strip addressId - it belongs to the primary applicant's own address record, not this
            // one, and leaving it set would permanently hide the "same as current address" checkbox
            // (gated on !mailingAddressData.addressId) even after this box is unchecked.
            this.mailingAddressData = {...this.primaryMailingAddress, addressId: undefined};
        } else if (this.preSameAsPrimaryMailingAddressSnapshot) {
            this.mailingAddressData = this.preSameAsPrimaryMailingAddressSnapshot;
            this.preSameAsPrimaryMailingAddressSnapshot = null;
        }
    }

    getResidenceFormGroup(residence) {
        const residenceFormGroup = {
            addressData: [residence.address],
            residenceId: [residence.residenceId],
            streetAddress: [residence.address?.streetAddress, Validators.required],
            city: [residence.address?.city, Validators.required],
            state: [residence.address?.state, Validators.required],
            zipCode: [residence.address?.zipCode, Validators.required],
            county: [residence.address?.county],
            country: [residence.address?.country],
            residenceLength: [residence.length, (this.finalSubmitPageConfig.residencyHistory) ? Validators.required : []],
            residenceStatus: [residence.status || null, (this.finalSubmitPageConfig.residencyHistory) ? Validators.required : []]
        };

        return this.fb.group(residenceFormGroup);
    }

    createForm() {
        const residence = [];
        const currentResidence = {...this.applicant.residence, address: this.applicant.address || new AddressModel()};
        residence.push(this.getResidenceFormGroup(currentResidence));

        this.applicant.pastResidenceDetails?.forEach((pResidence, i) => {
            residence.push(this.getResidenceFormGroup(pResidence));
        });

        this.residenceForm = this.fb.group({
            residenceAdd: this.fb.array(residence)
        });

        const mailingAddresses: AddressModel[] = this.applicant.additionalAddressDetails || [];
        const mailingAddressData = mailingAddresses.find(item => item.addressUsage === 'COMMUNICATION');
        const hasExistingMailingAddress = !!(mailingAddressData && mailingAddressData.addressId);
        this.sameMailAddress = !hasExistingMailingAddress;
        this.mailingAddressData = mailingAddressData || new AddressModel();

        // Default the "same as primary" checkbox to checked when the feature is enabled, as long as
        // the JO doesn't already have their own distinct saved mailing address - don't silently
        // override a mailing address they previously entered on a return visit.
        if (this.isCoBo && this.appConfig.coBoMailingAddressSameAsPrimary && !hasExistingMailingAddress
            && this.primaryMailingAddress?.streetAddress) {
            this.sameMailingAsPrimary = true;
            this.onSameAsPrimaryAddressChange();
        }

        this.detachAddressDataValidators();
    }

    private detachAddressDataValidators() {
        // AddressComponent registers itself as NG_VALIDATORS on every `formControlName="addressData"`
        // binding. Its validator reads mutable internal state (`this.value`) which can desync after
        // the edit-address dialog flow and leave `addressData` stuck with {required} or {invalid}
        // even when the patched streetAddress/city/state/zipCode siblings are populated. Those
        // sibling controls (with Validators.required) are sufficient for residenceForm validity,
        // and the in-field mat-error UI is driven by the AddressComponent's internal fullAddress
        // NgModel validator — not by the addressData FormControl — so clearing here is safe.
        // Defer to next microtask so it runs after Angular binds the CVA/NG_VALIDATORS.
        Promise.resolve().then(() => {
            this.residenceAddArray.controls.forEach(group => {
                const addressDataCtrl = group.get('addressData');
                if (addressDataCtrl) {
                    addressDataCtrl.clearValidators();
                    addressDataCtrl.updateValueAndValidity({emitEvent: false});
                }
            });
        });
    }

    addressChanged(event: AddressModel, formGroup: any) {
        if (formGroup && formGroup.patchValue) {
            formGroup.patchValue(event);
        }
    }

    checkResidenceYears(event, index) {
        if (index !== 0 || this.finalSubmitPages.residenceHistoryDuration <= 0) {
            return;
        }
        const control = this.residenceForm.controls.residenceAdd as FormArray;
        control.controls[index].get('residenceLength').markAsTouched();

        if (control.controls[index].get('residenceLength').invalid || control.controls[index].get('residenceLength').value === 0) {
            return;
        }
        const residenceFormValues = this.residenceForm.getRawValue();

        const duration = +residenceFormValues.residenceAdd[index].residenceLength || 0;

        if (duration < this.finalSubmitPages.residenceHistoryDuration) {
            if (control.length < 2) {
                control.push(this.fb.group({
                    addressData: [new AddressModel()],
                    streetAddress: ['', Validators.required],
                    city: ['', Validators.required],
                    state: ['', Validators.required],
                    zipCode: ['', Validators.required],
                    county: [''],
                    country: [''],
                    residenceLength: [0, Validators.required],
                    residenceStatus: [null, Validators.required]
                }));
                this.detachAddressDataValidators();
            }
        } else {
            if (control.length > 1) {
                control.removeAt(control.length - 1);
            }
        }
    }

    removeResidence(index) {
        if (index !== 0) {
            this.residenceAddArray.controls[index - 1].get('residenceLength').patchValue(null);
        }
        this.residenceAddArray.removeAt(index);
    }

}
